import { FolderBreadcrumbText } from '../../components/Molecules';
import { RegionsCode } from '../../constants/region';
import {
  ClubTemplate,
  // SpaceMembershipRole,
  SpacePrivacyPolicy,
  SpaceType,
} from '../../constants/space';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { createI18nInfo } from '../../utils/i18n';
import { sumNumberRecords } from '../../utils/object';
import {
  AttachmentImage,
  AttachmentImageExample,
  CarouselItem,
  EducationLevel,
  I18n,
  I18nLocale,
  Icon,
  Locale,
  Period,
  PeriodUnit,
  Tag,
  TtsPreferences,
  Uuid,
} from '../Base';
import { RepositoryElement } from '../Repository';
import { EnumAgeGroup, Gender, WebBoosterType } from '../UserStore';

export type I18nClassroomInfo = I18n<{
  name: string;
  shortDescription: string | null;
  description: string | null;
  objectives: string | null;
  carousel: CarouselItem[];
}>;

export enum SpaceState {
  Available = 'available',
  Archived = 'archived',
}

export enum SpaceCarouselDisplay {
  IsometricMap = 'isometricMap',
  Carousel = 'carousel',
}

interface ChallengeAnimation {
  fileIds: Uuid[];
  frameDuration: number;
  repeats?: number;
}

interface DefaultLearningSchedule {
  period: Period;
  days: number[];
}

export enum AlbumPartitionDisplay {
  Hidden = 'hidden',
  Albums = 'gameSets', // TODO: refactor backend
  Partition = 'partition',
}

type I18nGameSetPartitionInfo = I18n<{
  name: string;
}>;

export interface AlbumPartition {
  partitionId: string; // TODO: regex?
  display: AlbumPartitionDisplay;
  webBoosterType: WebBoosterType;
  i18nGameSetPartitionInfo: I18nGameSetPartitionInfo;
  icon?: Icon;
}

export enum SpaceTabPublisher {
  Public = 'public',
  Followers = 'followers',
  Members = 'members',
  Moderators = 'moderators',
  Admins = 'admins',
}

export interface SpaceTabCategory {
  name: string; // 1-50 chars
  isPreset: boolean;
  postCount: number;
}

interface SpaceIncludedRegionRule {
  included: RegionsCode[];
  excluded?: undefined;
}
interface SpaceExcludedRegionRule {
  included?: undefined;
  excluded: RegionsCode[];
}

export type SpaceRegionRule = SpaceIncludedRegionRule | SpaceExcludedRegionRule;

export interface SpaceTab {
  name: string; // 1-50 chars
  isHidden: boolean;
  publisher: SpaceTabPublisher;
  postCount: number;
  categories: Record<string, SpaceTabCategory>; // key = categoryId [0-9a-f]{8}
}

export interface SpaceAudience {
  anyone: boolean;
  ageGroup?: EnumAgeGroup[];
  level?: EducationLevel[];
  gender?: Gender[];
  region?: RegionsCode[];
}

type StatisticsKey =
  | 'rootCount'
  | 'adminCount'
  | 'moderatorCount'
  | 'memberCount'
  | 'followerCount'
  | 'waitingForApprovalMemberCount'
  | 'invitedMemberCount'
  | 'blockedByGroupMemberCount'
  | 'likeCount'
  | 'classroomChannelPublicPostCount'
  | 'classroomChannelFollowersPostCount'
  | 'classroomChannelMembersPostCount'
  | 'classroomChannelModeratorsPostCount'
  | 'classroomChannelAdminsPostCount'
  | 'classroomChannelRootPostCount'
  | 'storedImageCount'
  | 'storedImageSize'
  | 'storedIconCount'
  | 'storedIconSize'
  | 'storedAudioCount'
  | 'storedAudioSize'
  | 'storedVideoCount'
  | 'storedVideoSize'
  | 'createdGameCollectionCount'
  | 'createdGameCount'
  | 'createdAssistedGameCount'
  | 'createdSlideshowCount'
  | 'gameSetCount'
  | 'lessonCount'
  | 'itemCount'
  | 'gameCount'
  | 'assistedGameCount'
  | 'slideshowCount'
  | 'pathChallengeCount'
  | 'superChallengeCount'
  | 'multiplayerGameCount'
  | 'puzzlePieceCount'
  | 'playCount'
  | 'contentLikeCount'
  | 'savedNormalReplyCount'
  | 'savedPerformanceReplyCount'
  | 'savedShopReplyCount'
  | 'cassavaSeedCount'
  | 'harvestedEsgPlantCount'
  | 'angelFollowerCount';

type SpaceStatistics = Partial<Record<StatisticsKey, number>>;

type CustomTextField = string | { text: string };

enum NameOfClassroomMembers {
  club = 'Admin, Moderator, Member',
  classroom = 'Teacher, Teaching Assistant, Learner',
  corporate = 'Manager, Leader, Teammate',
  group = 'Leader, Co-leader, Group Member',
}

interface LearningFeatures {
  isEnabled: boolean;
  objectives: boolean;
  membersPerformance: boolean;
  memoryIndex: boolean;
  folderStructureForGameSets: boolean;
}

interface CourseFeatures {
  isEnabled: boolean;
  startAndEndLabelForGameSets: boolean;
  showOlderGameSetsAtTop: boolean;
  hideCreationDateOfGameSets: boolean;
  courseCertificate?: boolean;
}

interface PromotionFeatures {
  isEnabled: boolean;
  ads: boolean;
  customRewards: boolean;
}

interface ReligiousStudyFeatures {
  isEnabled: boolean;
  // religion: Religion<Image>;
  verseOnPosts: boolean;
  dailyPrayerPosts: boolean;
  prayerRequestPosts: boolean;
  dailyVersePosts: boolean;
  testimonialPosts: boolean;
  bibleVerseSpeciallyForYou: boolean;
  // customLikeAndCheer: CustomLikeAndCheer<Image>;
}

interface CorporateFeatures {
  isEnabled: boolean;
  coreValues: boolean;
  membersJobTitle?: boolean;
  workingReportForms?: boolean;
  appraisalForms?: boolean;
}
interface Features {
  template: ClubTemplate;
  thanksgivingPosts?: boolean;
  targetPosts?: boolean;
  tabWithGameSets: boolean;
  customTextField: CustomTextField;
  nameOfClassroomMembers: NameOfClassroomMembers;
  classroomTabs: string[];
  learningFeatures: LearningFeatures;
  courseFeatures: CourseFeatures;
  promotionFeatures: PromotionFeatures;
  religiousStudyFeatures: ReligiousStudyFeatures;
  corporateFeatures: CorporateFeatures;
}

export interface SpaceJson<T = number> {
  readonly classroomId: Uuid; //identifier
  createdAt: T;
  lastModifiedAt: T;
  lastNotifiedAt: T;
  isSuspended?: boolean;
  isUpgraded?: boolean;
  i18nClassroomInfo: I18nClassroomInfo;
  type: SpaceType;
  state: SpaceState;
  precedence: number;
  icon: AttachmentImage;
  banner: AttachmentImage | null;
  display: SpaceCarouselDisplay | null;
  challengeAnimation: ChallengeAnimation | null;
  region: SpaceRegionRule | null;
  topics: string[]; // size = [0, 3]
  audience: SpaceAudience;
  tags: Tag[];
  learningLocaleCount: number | null;
  locales: I18nLocale[];
  ttsPreferences: TtsPreferences;
  features: Features;
  privacyPolicy: SpacePrivacyPolicy;
  defaultLearningSchedule: DefaultLearningSchedule;
  gameSetPartitions: AlbumPartition[];
  tabs: Record<string, SpaceTab>; // key = tabId [0-9]{2}
  relatedClassroomIds: Uuid[];
  statistics: SpaceStatistics;
  memberPopulation: Record<string, number>;
  membershipTier?: boolean;
  showFeaturesTemplate?: boolean;
  showRoot?: boolean;
  showAdminAndModerator?: boolean;
  showRootClassrooms?: boolean;
  // var theme: Theme<DownloadableAttachmentImage>
  // var leaderboard: SpaceLeaderboard<DownloadableAttachmentImage, DownloadableAttachmentVideo>?  // null

  // isCertified?: boolean; // not on backend anymore
}

export class Space extends RepositoryElement implements SpaceJson<Date> {
  readonly classroomId!: Uuid; //identifier
  createdAt!: Date;
  lastModifiedAt!: Date;
  lastNotifiedAt!: Date;
  isSuspended?: boolean;
  isUpgraded?: boolean;
  i18nClassroomInfo!: I18nClassroomInfo;
  type!: SpaceType;
  state!: SpaceState;
  precedence!: number;
  icon!: AttachmentImage;
  banner!: AttachmentImage | null;
  display!: SpaceCarouselDisplay | null;
  challengeAnimation!: ChallengeAnimation | null;
  region!: SpaceRegionRule | null;
  audience!: SpaceAudience;
  topics!: string[]; // size = [0, 3]
  tags!: Tag[];
  learningLocaleCount!: number | null;
  locales!: I18nLocale[];
  ttsPreferences!: TtsPreferences;
  privacyPolicy!: SpacePrivacyPolicy;
  defaultLearningSchedule!: DefaultLearningSchedule;
  gameSetPartitions!: AlbumPartition[];
  tabs!: Record<string, SpaceTab>; // key = tabId [0-9]{2}
  relatedClassroomIds!: Uuid[];
  statistics!: SpaceStatistics;
  memberPopulation!: Record<string, number>;
  features!: Features;
  membershipTier?: boolean | undefined;
  showAdminAndModerator?: boolean | undefined;
  showFeaturesTemplate?: boolean | undefined;
  showRoot?: boolean | undefined;
  showRootClassrooms?: boolean | undefined;

  // isCertified?: boolean; // not on backend anymore

  constructor(space: SpaceJson<Date | number>) {
    super();
    // this.lastModifiedAt = new Date();
    Object.assign(this, deepTransformTimestamp(space));
  }

  id(): string {
    return this.classroomId;
  }

  toJSON(): SpaceJson {
    return deepTransformDate(this) as unknown as SpaceJson;
  }

  get isHome() {
    return this.type === SpaceType.Home;
  }

  // get isCompetition() {
  //   return (
  //     this.type === SpaceType.Competition || this.type === SpaceType.Tournament
  //   );
  // }

  get isMultiLang(): boolean {
    return (this.learningLocaleCount ?? 1) > 1;
  }

  // get isSelfStudy(): boolean {
  //   return this.type === SpaceType.SelfStudy;
  // }

  incrementStatistics(statistics: SpaceStatistics): void {
    sumNumberRecords(this.statistics, statistics);
  }

  static postCount(
    statistics: Partial<Record<StatisticsKey, number>>,
    // role: SpaceMembershipRole | undefined,
  ) {
    const sum = statistics.classroomChannelPublicPostCount ?? 0;

    // switch (role) {
    //   case SpaceMembershipRole.Admin:
    //     sum += statistics.classroomChannelAdminsPostCount ?? 0;
    //   // eslint-disable-next-line no-fallthrough
    //   case SpaceMembershipRole.Moderator:
    //     sum += statistics.classroomChannelModeratorsPostCount ?? 0;
    //   // eslint-disable-next-line no-fallthrough
    //   case SpaceMembershipRole.Member:
    //     sum += statistics.classroomChannelMembersPostCount ?? 0;
    //   // eslint-disable-next-line no-fallthrough
    //   case SpaceMembershipRole.Follower:
    //     sum += statistics.classroomChannelFollowersPostCount ?? 0;
    // }

    return sum;
  }

  get roleTotal() {
    return (
      (this.statistics.rootCount ?? 0) +
      (this.statistics.adminCount ?? 0) +
      (this.statistics.moderatorCount ?? 0)
    );
  }

  get memberTotal() {
    return (
      this.roleTotal +
      (this.statistics.memberCount ?? 0) -
      (this.statistics.rootCount ?? 0) //not include the club owner in the member count
    );
  }

  get followerTotal() {
    return this.memberTotal + (this.statistics.followerCount ?? 0);
  }

  get hasRedeemShop(): boolean {
    return this.features.classroomTabs.includes('shop');
  }

  getDefaultPartitions(
    webBoosterType: WebBoosterType | undefined,
  ): AlbumPartition[] {
    const gameSetPartitions = this.gameSetPartitions.filter(
      (partition) =>
        partition.display !== AlbumPartitionDisplay.Hidden &&
        (!webBoosterType || partition.webBoosterType === webBoosterType),
    );
    if (gameSetPartitions.length === 0 && webBoosterType) {
      return [
        {
          partitionId: `${webBoosterType}-${FolderBreadcrumbText.Default}`,
          display: AlbumPartitionDisplay.Partition,
          webBoosterType,
          i18nGameSetPartitionInfo: createI18nInfo(
            I18nLocale.US,
            { name: 'Default Folder' },
            // {
            //   [I18nLocale.CN]: { name: '默认文件夹' },
            //   [I18nLocale.HK]: { name: '預設資料夾' },
            //   [I18nLocale.TW]: { name: '預設資料夾' },
            //   [I18nLocale.JP]: { name: 'デフォルトのフォルダー' },
            // },
          ),
        },
      ];
    }
    return gameSetPartitions;
  }

  getAllPartitions(
    webBoosterType: WebBoosterType | undefined,
  ): AlbumPartition[] {
    const gameSetPartitions = this.gameSetPartitions.filter(
      (partition) =>
        partition.display !== AlbumPartitionDisplay.Hidden &&
        (!webBoosterType || partition.webBoosterType === webBoosterType),
    );
    const allPartition: AlbumPartition = {
      partitionId: `${webBoosterType}-${FolderBreadcrumbText.All}`,
      display: AlbumPartitionDisplay.Partition,
      webBoosterType: webBoosterType ?? WebBoosterType.Normal,
      i18nGameSetPartitionInfo: createI18nInfo(I18nLocale.US, {
        name: 'All Content',
      }),
    };
    if (gameSetPartitions.length === 0 && webBoosterType) {
      return [
        allPartition,
        {
          partitionId: `${webBoosterType}-${FolderBreadcrumbText.Default}`,
          display: AlbumPartitionDisplay.Partition,
          webBoosterType,
          i18nGameSetPartitionInfo: createI18nInfo(
            I18nLocale.US,
            { name: 'Default Folder' },
            // {
            //   [I18nLocale.CN]: { name: '默认文件夹' },
            //   [I18nLocale.HK]: { name: '預設資料夾' },
            //   [I18nLocale.TW]: { name: '預設資料夾' },
            //   [I18nLocale.JP]: { name: 'デフォルトのフォルダー' },
            // },
          ),
        },
      ];
    }
    return [allPartition, ...gameSetPartitions];
  }
}

export const SpaceExample: Space = new Space({
  classroomId: 'classroomId',
  createdAt: new Date(),
  lastModifiedAt: new Date(),
  lastNotifiedAt: new Date(),
  i18nClassroomInfo: {
    resources: {
      'en-US': {
        name: 'Classroom',
        shortDescription: 'shortDescription',
        description: 'description',
        objectives: 'objectives',
        carousel: [],
      },
    },
    defaultLocale: Locale.US,
  },
  type: SpaceType.Home,
  state: SpaceState.Available,
  display: SpaceCarouselDisplay.IsometricMap,
  precedence: 0,
  icon: AttachmentImageExample,
  banner: null,
  challengeAnimation: null,
  audience: { anyone: true },
  region: { included: [RegionsCode.HK] },
  topics: [],
  tags: [],
  learningLocaleCount: null,
  locales: [],
  ttsPreferences: {
    en: Locale.AU,
    zh: Locale.HK,
  },
  privacyPolicy: SpacePrivacyPolicy.Public,
  defaultLearningSchedule: {
    days: [1, 3, 5],
    period: {
      unit: PeriodUnit.Month,
      quantity: 3,
    },
  },
  gameSetPartitions: [],
  tabs: {},
  relatedClassroomIds: [],
  statistics: {},
  memberPopulation: {},
  features: {
    template: ClubTemplate.General,
    thanksgivingPosts: false,
    targetPosts: false,
    tabWithGameSets: true,
    customTextField: 'Share joy with others!',
    nameOfClassroomMembers: NameOfClassroomMembers.club,
    classroomTabs: ['home', 'gameSet', 'post', 'shop'],
    learningFeatures: {
      isEnabled: true,
      objectives: true,
      membersPerformance: true,
      memoryIndex: true,
      folderStructureForGameSets: true,
    },
    courseFeatures: {
      isEnabled: true,
      startAndEndLabelForGameSets: true,
      showOlderGameSetsAtTop: true,
      hideCreationDateOfGameSets: true,
      courseCertificate: true,
    },
    promotionFeatures: {
      isEnabled: false,
      ads: false,
      customRewards: false,
    },
    religiousStudyFeatures: {
      isEnabled: false,
      // religion: .default("christian"),
      verseOnPosts: true,
      dailyPrayerPosts: true,
      prayerRequestPosts: true,
      dailyVersePosts: false,
      testimonialPosts: true,
      bibleVerseSpeciallyForYou: false,
      // customLikeAndCheer: .init(
      //     isEnabled: false,
      //     like: nil,
      //     cheer: nil
      // )
    },
    corporateFeatures: {
      isEnabled: false,
      coreValues: false,
      membersJobTitle: false,
      workingReportForms: false,
      appraisalForms: false,
    },
  },
});
